import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CapabilitiesContext = createContext(null);

/**
 * Provides the current user's effective capability flags (union of all their
 * roles, per the admin-editable capability matrix stored on the backend).
 *
 * Consumers do `const { can } = useCapabilities();` and call `can("create_projects")`.
 * If capabilities haven't loaded yet, `can()` returns `false` for safety
 * (better to hide a button briefly than to leak an unauthorized action).
 */
export function CapabilitiesProvider({ children }) {
  const { user } = useAuth();
  const [caps, setCaps] = useState({});
  const [capsUserId, setCapsUserId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setCaps({}); setCapsUserId(null); setLoaded(true); return; }
    try {
      const r = await api.get("/me/capabilities");
      setCaps(r.data?.capabilities || {});
      setCapsUserId(user.id || null);
    } catch {
      setCaps({});
      setCapsUserId(null);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Derived "loaded": we only consider caps loaded when they were fetched
  // for the *current* user. Prevents route guards from making a decision
  // based on a previous user's caps (or an empty pre-login cache) during
  // the brief window right after login/logout before refresh() completes.
  const currentUserId = user && user.id ? user.id : (user === false ? "__anon__" : null);
  const isLoaded = loaded && (
    !user
      ? true
      : capsUserId === currentUserId
  );

  const can = useCallback((key) => !!caps[key], [caps]);

  return (
    <CapabilitiesContext.Provider value={{ caps, can, loaded: isLoaded, refresh }}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

export function useCapabilities() {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) {
    // Safe fallback if provider not wired yet
    return { caps: {}, can: () => false, loaded: false, refresh: () => {} };
  }
  return ctx;
}
